<template>
    <Card
        icon="fas fa-cog"
        bg-color="tw:bg-[var(--sitecolor)]"
        title-color="white"
        :title="'Configuración'"
        padding="0"
    >
        <div class="tw:p-6 tw:space-y-4">
            <div class="tw:space-y-3">
                <ToggleSwitch
                    :value="localData.activar_enlace_redireccion"
                    @input="val => localData.activar_enlace_redireccion = val"
                    variant="primary"
                    id="checkbox_redireccion_otra_web"
                >
                    {{ 'Añadir redirección a otra web en el botón de compra' }}
                </ToggleSwitch>

                <div v-if="localData.activar_enlace_redireccion" class="tw:ml-6" ref="enlaceRedireccionSection">
                    <BaseInputText
                        id="redirigir-otra-web"
                        name="redireccion_otra_web"
                        :label="'Introduce una url'"
                        :value="localData.enlace_redireccion"
                        @input="val => localData.enlace_redireccion = val"
                    />
                </div>
            </div>

            <div class="tw:border-t tw:border-gray-100"></div>

            <div class="tw:space-y-3">
                <ToggleSwitch
                    :value="localData.comprobacion_butacas"
                    @input="val => localData.comprobacion_butacas = val"
                    variant="primary"
                    id="comprobacion_butacas"
                >
                    {{ 'Comprobar huecos entre butacas' }}
                </ToggleSwitch>

                <div v-if="localData.recinto">
                    <ToggleSwitch
                        :value="localData.tiene_plazas_extra"
                        @input="val => localData.tiene_plazas_extra = val"
                        variant="primary"
                        id="tiene_plazas_extra"
                    >
                        {{ 'Tiene plazas extra' }}
                    </ToggleSwitch>

                    <div v-if="localData.tiene_plazas_extra" class="tw:ml-6 tw:mt-3" ref="plazasExtraSection">
                        <BaseInputText
                            id="texto_producto_extra"
                            name="texto producto extra"
                            :label="'Texto del producto extra'"
                            :value="localData.texto_producto_extra"
                            rules="required"
                            @input="val => localData.texto_producto_extra = val"
                        />
                    </div>
                </div>

                <ToggleSwitch
                    :value="localData.compra_privada"
                    @input="val => localData.compra_privada = val"
                    variant="primary"
                    id="compra_privada"
                >
                    {{ 'Compra privada' }}
                    <Tooltip
                        :text="'Solo podrán acceder a la venta un listado de correos concretos. Los correos permitidos para la compra de entradas online se añadirán en la SESIÓN - COMPRADORES PERMITIDOS'"
                        variant="success"
                        class="tw:normal-case"
                    />
                </ToggleSwitch>

                <ToggleSwitch
                    :value="localData.calendario"
                    @input="val => localData.calendario = val"
                    variant="primary"
                    id="calendario"
                >
                    {{ 'Evento tipo calendario' }}
                </ToggleSwitch>

                <ToggleSwitch
                    v-if="canEditKiosk"
                    :value="localData.show_in_kiosk"
                    @input="val => set(localData, 'show_in_kiosk', val)"
                    variant="primary"
                    id="show_in_kiosk"
                >
                    {{ 'Mostrar en kiosco' }}
                </ToggleSwitch>
            </div>

            <div class="tw:border-t tw:border-gray-100"></div>

            <div class="tw:space-y-3">
                <template v-if="isTeatros">
                    <ToggleSwitch
                        :value="localData.evento_barra"
                        @input="val => localData.evento_barra = val"
                        variant="primary"
                        id="checkbox_evento_barra"
                    >
                        {{ 'Evento barra' }}
                    </ToggleSwitch>
                </template>

                <template v-if="isDeportes">
                    <ToggleSwitch
                        :value="localData.experiencia"
                        @input="val => set(localData, 'experiencia', val)"
                        variant="primary"
                        id="checkbox_evento_experiencia"
                    >
                        {{ 'Experiencia' }}
                        <Tooltip
                            :text="'Este evento solo aparecerá en el apartado de experiencias.'"
                            variant="success"
                            class="tw:normal-case"
                        />
                    </ToggleSwitch>
                </template>

                <ToggleSwitch
                    :value="localData.reserva_instalaciones"
                    @input="val => localData.reserva_instalaciones = val"
                    variant="primary"
                    id="checkbox_reserva_instalaciones"
                >
                    {{ 'Reserva de instalaciones' }}
                </ToggleSwitch>

                <div v-if="localData.reserva_instalaciones" class="tw:ml-6 tw:mt-3" ref="reglamentoSection">
                    <ValidationProvider name="reglamento" v-slot="{ errors }">
                        <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">{{ 'Reglamento' }}</label>
                        <QuillEditor
                            ref="editorReglamento"
                            :value="localData.reglamento"
                            @input="val => localData.reglamento = val"
                        />
                        <span v-if="errors[0]" class="tw:text-xs tw:text-red-600">{{ errors[0] }}</span>
                    </ValidationProvider>
                </div>

                <ToggleSwitch
                    :value="localData.no_enviar_entradas"
                    @input="val => localData.no_enviar_entradas = val"
                    variant="primary"
                    id="checkbox_no_enviar_entradas"
                >
                    {{ 'Enviar entradas 72h antes del evento' }}
                    <Tooltip
                        :text="'Las entradas NO se enviarán antes de este plazo (72h) por ningún medio.'"
                        variant="success"
                        class="tw:normal-case"
                    />
                </ToggleSwitch>
            </div>
        </div>
    </Card>
</template>

<script setup>
    import { computed, inject, getCurrentInstance, ref, set } from 'vue';
    import { useScrollOnToggle } from '@/composables/common/useScrollOnToggle';
    import Card from '@/modules/admin/ui/Card.vue';
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import Tooltip from '@/modules/admin/ui/Tooltip.vue';
    import QuillEditor from '@/modules/ui/QuillEditor.vue';

    const TEATROS = 2;

    const localData = inject('eventoFormData');

    const props = defineProps({
        manager: { type: Object, default: () => ({}) }
    });

    const permisos = computed(() => {
        const instance = getCurrentInstance();
        return instance?.proxy?.$store?.getters?.getPermisos || {};
    });

    const isTeatros = computed(() => props.manager.tipo === TEATROS);
    const isDeportes = computed(() => props.manager.tipo === 1);
    // TODO(admin/kiosko): reintroduce a dedicated `eventos.kiosko` permission
    // once the backend exposes it. Until then, mirror EventosActuales.vue:46 —
    // anyone who can edit/crear eventos can flip the kiosk toggle.
    const canEditKiosk = computed(() => !!(permisos.value['eventos.editar'] || permisos.value['eventos.crear']));

    const enlaceRedireccionSection = ref(null);
    const plazasExtraSection = ref(null);
    const reglamentoSection = ref(null);
    const editorReglamento = ref(null);

    useScrollOnToggle(() => localData.activar_enlace_redireccion, enlaceRedireccionSection);
    useScrollOnToggle(() => localData.tiene_plazas_extra, plazasExtraSection);
    useScrollOnToggle(() => localData.reserva_instalaciones, reglamentoSection);
</script>
